<script setup lang="ts">
import { onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import { useGoogleCleanupStore } from "@/stores/googleCleanup";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import GoogleContactCleanupDialog from "@/components/lead/GoogleContactCleanupDialog.vue";
import type { LeadChain } from "@/api/types";

const auth = useAuthStore();
const ui = useUiStore();
const googleCleanup = useGoogleCleanupStore();
const router = useRouter();
const route = useRoute();

onMounted(async () => {
  await auth.initFromSession();
  window.addEventListener("keydown", onEscape);
  window.addEventListener("auth:logout", onAuthLogout as EventListener);
  window.addEventListener("api:rate-limit", onRateLimit as EventListener);
  window.addEventListener("lead:google-contact-prompt", onGoogleContactPrompt as EventListener);
  window.addEventListener("lead:chain", onLeadChain as EventListener);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onEscape);
  window.removeEventListener("auth:logout", onAuthLogout as EventListener);
  window.removeEventListener("api:rate-limit", onRateLimit as EventListener);
  window.removeEventListener("lead:google-contact-prompt", onGoogleContactPrompt as EventListener);
  window.removeEventListener("lead:chain", onLeadChain as EventListener);
});

function onEscape(e: KeyboardEvent) {
  if (e.key === "Escape" && ui.sidePanelLeadId !== null) {
    ui.closeSidePanel();
  }
}

function onAuthLogout(e: CustomEvent<{ reason?: string }>) {
  // A3 polish: zamiast cichego logoutu, pokaż toast + redirect.
  const wasAuthenticated = auth.isAuthenticated;
  auth.user = null;
  if (wasAuthenticated && route.name !== "login") {
    if (e.detail?.reason === "session_expired") {
      ui.pushToast("warn", "Sesja wygasła — zaloguj się ponownie", undefined, 8000);
    }
    router.push({ name: "login", query: { redirect: route.fullPath } });
  }
}

function onRateLimit(e: CustomEvent<{ retryMinutes: number }>) {
  // A3 polish: rate-limit 429 z czytelnym komunikatem.
  const mins = e.detail?.retryMinutes ?? 15;
  ui.pushToast(
    "warn",
    `Za dużo prób w krótkim czasie. Spróbuj ponownie za ${mins} ${mins === 1 ? "minutę" : "minut"}.`,
    undefined,
    10000,
  );
}

function onGoogleContactPrompt(e: CustomEvent<{ leadId: number }>) {
  if (e.detail?.leadId != null) googleCleanup.open(e.detail.leadId);
}

// Łańcuch Rekrutacja → Wdrożenie → Zespół: komunikat z serwera, link do karty, na którą prowadzi przejście.
function onLeadChain(e: CustomEvent<LeadChain>) {
  const c = e.detail;
  if (!c) return;
  const ok = c.status === "created" || c.status === "moved";
  const leadId = c.lead_id;
  const action = leadId != null && c.status !== "moved"
    ? { label: "Otwórz kartę", handler: () => { router.push({ name: "szansa-detail", params: { id: leadId } }); } }
    : undefined;
  ui.pushToast(ok ? "success" : "warn", c.message, action, 10000);
}
</script>

<template>
  <RouterView />
  <ToastContainer />
  <GoogleContactCleanupDialog />
</template>
